import React, { useEffect, useRef, useState } from 'react';
import shaka from 'shaka-player/dist/shaka-player.compiled.js';
import { useNavigate } from 'react-router-dom';
import { MdForward10, MdSettings, MdFullscreen, MdFullscreenExit, MdPause, MdPlayArrow, MdReplay10, MdVolumeDown, MdVolumeMute, MdVolumeUp } from 'react-icons/md';
import SVGLOGO from '../__DEV/Old Design/OldComponents/logo';


shaka.polyfill.installAll();

const VideoPlayerDrm = ({ src, licenseToken   }) => {

const navigate = useNavigate();
const containerRef = useRef(null);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedAmount, setBufferedAmount] = useState(0);

  // Resolution State variables
  const [resolutions, setResolutions] = useState([]); // Stores objects: { height, bandwidth }
  const [currentResolution, setCurrentResolution] = useState('Auto');
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  
  // State to hold explicit errors right on phone screen
  const [debugError, setDebugError] = useState(null);
  
  // ADDED: UI text state handling indicator for clipboard verification feedback
  const [copyStatus, setCopyStatus] = useState("Copy Error");

  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const checkPlatform = () => {
      const platform = navigator.userAgentData?.platform || navigator.platform || '';
      const userAgent = navigator.userAgent;

      // Regular check for iPhone/iPod
      const isAppleMobile = /iPhone|iPad|iPod/.test(userAgent) || /iPhone|iPad|iPod/.test(platform);
      
      // Modern iPad check (which claims to be 'MacIntel' but has touch support)
      const isMaciPad = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
       
      if (isAppleMobile || isMaciPad) {
        setIsIOS(true);
      }
    };

    checkPlatform();
  }, []);

  // NEW: Keeps track of live variant bandwidth during Auto mode adjustments
  const [activeAutoBandwidth, setActiveAutoBandwidth] = useState(0);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Math calculation helper utility
  const calculateApproxSize = (bandwidth, videoDuration) => {
    if (!bandwidth || !videoDuration || isNaN(videoDuration)) return null;
    const totalBits = bandwidth * videoDuration;
    const totalMB = totalBits / (8 * 1024 * 1024);
    if (totalMB >= 1024) {
      return `${(totalMB / 1024).toFixed(1)} GB`;
    }
    return `${Math.round(totalMB)} MB`;
  };

  // FIXED: Added conditional protection layout check for iOS native wrapper pipelines
  const updateAvailableResolutions = (player) => {
    if (!player) return;
    
    // Safely pull variant track arrays matching the active MSE controller engine context
    const tracks = typeof player.getVariantTracks === 'function' ? player.getVariantTracks() : [];
    
    // FALLBACK FOR NATIVE HLS PLAYBACK ENGINE (iOS Environment)
    if (tracks.length === 0) {
      // Hardcode common resolution profile configurations natively found on your transcoder configurations
      // This populates the UI container select choices for your mobile clients cleanly
     /* setResolutions([
        { height: 1080, bandwidth: 4500000, isNativeFallback: true },
        { height: 720,  bandwidth: 2200000, isNativeFallback: true },
        { height: 480,  bandwidth: 1000000, isNativeFallback: true },
        { height: 360,  bandwidth: 600000,  isNativeFallback: true },
        { height: 240,  bandwidth: 350000,  isNativeFallback: true }
      ]);*/
      return;
    }
    
    const uniqueMap = new Map();
    tracks.forEach(track => {
      if (track.height && (!uniqueMap.has(track.height) || track.bandwidth > uniqueMap.get(track.height).bandwidth)) {
        uniqueMap.set(track.height, { height: track.height, bandwidth: track.bandwidth });
      }
    });

    const uniqueTracks = Array.from(uniqueMap.values());
    uniqueTracks.sort((a, b) => b.height - a.height);
    
    setResolutions(uniqueTracks);
  };

  const handleCopyErrorDetails = () => {
    if (!debugError) return;

    const textToCopy = `[DRM RUNTIME ERROR ANALYSIS RECORD]
-------------------------------------------
Context stage: ${debugError.context}
Error Code   : ${debugError.code}
Details      : ${debugError.message}
User Agent   : ${debugError.userAgent}
Timestamp    : ${new Date().toISOString()}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setCopyStatus("Copied! ✓");
          setTimeout(() => setCopyStatus("Copy Error"), 2000);
        })
        .catch(() => executeTextAreaFallback(textToCopy)); 
    } else {
      executeTextAreaFallback(textToCopy); 
    }
  };

  const executeTextAreaFallback = (text) => {
    try {
      if (window.ClipboardItem) {
        const blob = new Blob([text], { type: 'text/plain' });
        const item = new ClipboardItem({ 'text/plain': blob });
        
        navigator.clipboard.write([item]).then(() => {
          setCopyStatus("Copied! ✓");
          setTimeout(() => setCopyStatus("Copy Error"), 2000);
        }).catch(() => {
          setCopyStatus("Failed ✕");
        });
        return;
      }

      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      
      setCopyStatus(successful ? "Copied! ✓" : "Failed ✕");
      setTimeout(() => setCopyStatus("Copy Error"), 2000);
    } catch (err) {
      setCopyStatus("Failed ✕");
      setTimeout(() => setCopyStatus("Copy Error"), 2000);
    }
  };

  const fairplayCertUrl = "https://ustathi.app/cert/fairplay.cer";
  
  useEffect(() => {   
    const initPlayer = async () => {
         if (playerRef.current) {
           await playerRef.current.destroy();
         }
   
         const triggerScreenError = (context, code, message = '') => {
           setDebugError({
             context,
             code,
             message,
             userAgent: navigator.userAgent
           });
         };

      const player = new shaka.Player();
      playerRef.current = player;
      
      player.addEventListener('error', (event) => {
        console.error('Shaka Error Code:', event.detail.code);
        triggerScreenError(
          'Shaka',
          event.detail.code,
          JSON.stringify(event.detail.data)
        );
      });

      player.addEventListener('trackschanged', () => updateAvailableResolutions(player));
      
      player.addEventListener('variantchanged', (event) => {
        if (event.track) {
          setActiveAutoBandwidth(event.track.bandwidth);
          if (player.getConfiguration().abr.enabled) {
            setCurrentResolution(`Auto (${event.track.height}p)`);
          }
        }
      });

      const videoElement = videoRef.current;
      const handleNativeVideoError = () => {
        if (videoElement && videoElement.error) {
          triggerScreenError(
            'Native HTML5 Video Element Context', 
            videoElement.error.code, 
            videoElement.error.message || 'Codec failure or FairPlay key deployment rejection.'
          );
        }
      };
      
      // FIXED: Live dimensions resize sync listener for Apple platforms running in native ABR Mode
      const handleNativeResize = () => {
        if (playerRef.current && playerRef.current.getVariantTracks().length === 0) {
          if (videoElement && videoElement.videoHeight) {
            setCurrentResolution(`Auto (${videoElement.videoHeight}p)`);
          }
        }
      };
      
      if (videoElement) {
        videoElement.addEventListener('error', handleNativeVideoError);
        videoElement.addEventListener('resize', handleNativeResize);
      }

      try {
        await player.attach(videoElement);
        player.configure({
          streaming: {
            // OPTIONAL: Set to false if testing Forced ManagedMediaSource (MMS) rules on iOS 17.1+
            useNativeHlsForFairPlay: false 
          },
          drm: {
            servers: {
              'com.widevine.alpha': 'https://8d26f5bd.drm-widevine-licensing.axprod.net/AcquireLicense',
              'com.microsoft.playready': 'https://8d26f5bd.drm-playready-licensing.axprod.net/AcquireLicense',
              'com.apple.fps': 'https://8d26f5bd.drm-fairplay-licensing.axprod.net/AcquireLicense'
            },
            advanced: {
              'com.widevine.alpha': {
                videoRobustness: ['HW_SECURE_ALL'],
                audioRobustness: ['SW_SECURE_CRYPTO'],
              },
              'com.microsoft.playready': {
                videoRobustness: ['3000'], 
                audioRobustness: ['2000']
              },
              'com.apple.fps': {
                serverCertificateUri: fairplayCertUrl
              }
            }
          }
        });

        player.getNetworkingEngine().registerRequestFilter((type, request) => {
           if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
                request.headers['X-AxDRM-Message'] = licenseToken;
                if (request.uris[0].includes('playready')) {
                    request.headers['SOAPAction'] = '"http://schemas.microsoft.com/DRM/2007/03/protocols/AcquireLicense"';
                    request.headers['Content-Type'] = 'text/xml; charset=utf-8';
                }
            }
        });

        await player.load(src);
        updateAvailableResolutions(player);
         
        const variantTracks = player.getVariantTracks();
        if (variantTracks.length > 0) {
          const activeTrack = variantTracks.find(t => t.active);
          if (activeTrack) {
            setActiveAutoBandwidth(activeTrack.bandwidth);
            if (player.getConfiguration().abr.enabled) {
              setCurrentResolution(`Auto (${activeTrack.height}p)`);
            }
          }
        } else {
          // Default baseline print state for native elements
          setCurrentResolution('Auto');
        }

       } catch (error) {
        console.error('Shaka Init Error', error);
        triggerScreenError('Initialization Target Catch Block', error.code || 'N/A', error.message);
      }

      return () => {
        if (videoElement) {
          videoElement.removeEventListener('error', handleNativeVideoError);
          videoElement.removeEventListener('resize', handleNativeResize);
        }
      };
    };

    if (videoRef.current) {
      initPlayer();
    }
   
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [src, licenseToken]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const percent = (bufferedEnd / video.duration) * 100;
        setBufferedAmount(percent);
      }
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('playing', handlePlay);
    video.addEventListener('progress', handleProgress);
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', updateDuration);

    return () => {
      video.removeEventListener('progress', handleProgress);
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', updateDuration);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('playing', handlePlay);
    };
  }, []);

  const togglePlay = () => {
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  };

  const [isFullScreen, setIsFullScreen] = useState(false);
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);
  
  const toggleFullScreen = () => {
     const container = containerRef.current;
     const video = videoRef.current;
    
     const isCurrentlyFullScreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
     if(isIOS){
        if (!isCurrentlyFullScreen) {
          if (container.requestFullscreen) {
            container.requestFullscreen().catch((err) => {
              console.error(`Error entering full-screen: ${err.message}`);
            });
          } else if (container.webkitRequestFullscreen) {
            container.webkitRequestFullscreen();
          } else if (video && video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen();
          }
        } else {
          if (document.exitFullscreen) {
            document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          }
        }
      } else {
         if (!document.fullscreenElement) {
          container.requestFullscreen().catch((err) => {
            console.error(`Error entering full-screen: ${err.message}`);
          });
        } else {
          document.exitFullscreen();
        }
      }
  };

  const timerRef = useRef(null);
  const [showControls, setShowControls] = useState(true);

  const startTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!videoRef.current?.paused) {
        setShowControls(false);
        setShowQualityMenu(false);
      }
    }, 3000);
  };

  const handleInteraction = () => {
    setShowControls(true);
    startTimer();
  };

  const handleMouseEnterOverlay = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowControls(true);
  };

  const handleMouseLeaveOverlay = () => {
    startTimer();
  };

  useEffect(() => {
    startTimer();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const handleScrub = (e) => {
    const time = Number(e.target.value);
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleScrubEnd = () => {
    if (videoRef.current && videoRef.current.paused) {
      videoRef.current.play().catch(err => console.log("Blocked:", err));
    }
  };

  const playedPercent = (currentTime / duration) * 100 || 0;

  const toggleVideoClick = () => {
    if(!isPlaying) return;
    if(showControls) {
      setShowControls(false);
      setShowQualityMenu(false);
    } else {
      handleInteraction();
    }
  };

  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [prevVolume, setPrevVolume] = useState(1);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (isMuted) {
      const restoreVol = prevVolume > 0 ? prevVolume : 1;
      videoRef.current.volume = restoreVol;
      setVolume(restoreVol);
      setIsMuted(false);
    } else {
      setPrevVolume(volume);
      videoRef.current.volume = 0;
      setVolume(0);
      setIsMuted(true);
    }
  };

  const forward10Seconds = (e) => {
    e.stopPropagation(); 
    var time = Number(currentTime) + 10;
    if(time > duration) time = duration;
    setCurrentTime(time);
    if (videoRef.current) videoRef.current.currentTime = time;
  };

  const backward10Seconds = (e) => {
    e.stopPropagation(); 
    var time = Number(currentTime) - 10;
    if(time < 0) time = 0;
    setCurrentTime(time);
    if (videoRef.current) videoRef.current.currentTime = time;
  };

  // FIXED: Overhauled quality switching handler to support Native iOS Safari logic routing
  const handleQualityChange = (height) => {
    const player = playerRef.current;
    const videoElement = videoRef.current;
    if (!player || !videoElement) return;

    // Check if we are running in Native ABR mode on iOS
    const isNativeHls = player.getVariantTracks().length === 0;

    if (height === 'Auto') {
      if (isNativeHls) {
          videoElement.style.maxWidth = height === 'Auto' ? '100%' : `${(height * 16) / 9}px`;
  videoElement.style.maxHeight = height === 'Auto' ? '100%' : `${height}px`;
  setCurrentResolution(height === 'Auto' ? 'Auto' : `${height}p`);
        // Native Safari switches automatically via internally evaluated bitrates
        // Re-enabling standard auto sizes forces alignment
      } else {
        player.configure({ abr: { enabled: true } });
        const activeTrack = player.getVariantTracks().find(t => t.active);
        if (activeTrack) {
          setActiveAutoBandwidth(activeTrack.bandwidth);
          setCurrentResolution(`Auto (${activeTrack.height}p)`);
        }
      }
    } else {
      if (isNativeHls) {
        // Lock the HTML5 video container target decoding frame size to force iOS Safari's hand
  videoElement.style.maxWidth = height === 'Auto' ? '100%' : `${(height * 16) / 9}px`;
  videoElement.style.maxHeight = height === 'Auto' ? '100%' : `${height}px`;
  setCurrentResolution(height === 'Auto' ? 'Auto' : `${height}p`);
        setCurrentResolution(`${height}p`);
      } else {
        player.configure({ abr: { enabled: false } });
        const targetTrack = player.getVariantTracks().find(t => t.height === height);
        if (targetTrack) {
          player.selectVariantTrack(targetTrack, true);
          setActiveAutoBandwidth(targetTrack.bandwidth);
          setCurrentResolution(`${height}p`);
        }
      }
    }
    setShowQualityMenu(false);
  };

const lang = 'en';


  return (
    <div 
    dir='ltr'
    ref={containerRef} 
    className="relative group bg-black w-full flex flex-col select-none overflow-hidden "
    style={{ cursor: showControls ? 'default' : 'none'  }}
   >
     <video 
      className="w-full m-auto max-h-screen aspect-video"
      autoPlay={false}
      ref={videoRef} 
      playsInline 
      muted={isMuted}       
      webkit-playsinline="true"
     />

     {/* CENTER PLAY/PAUSE OVERLAY */}
      <div 
        onMouseMove={handleInteraction}
        onClick={toggleVideoClick}
        className={`absolute inset-0 transition-all duration-500 ${
          isPlaying ? (showControls && !showQualityMenu ? 'opacity-100' : 'opacity-0') : (showQualityMenu ? 'opacity-0' : 'opacity-100')
        } flex items-center gap-8 justify-center `}
      >
        <div onDoubleClick={backward10Seconds} className="w-1/3 flex items-center justify-center h-full pointer-events-auto">
          <MdReplay10 onClick={backward10Seconds} className="pointer-events-auto cursor-pointer hover:fill-[#dbdbdb]" color="#ffffff" size={30} />
        </div>

        <button 
          onClick={(e) => { e.stopPropagation(); togglePlay(); }}
          className="pointer-events-auto bg-black/40 hover:bg-black/60 text-white p-3 md:p-5 lg:p-6 rounded-full backdrop-blur-sm border border-white/20 active:scale-90"
        >
          {isPlaying ? <MdPause color="#ffffff" size={25} /> : <MdPlayArrow color="#ffffff" size={25} />}
        </button>

        <div onDoubleClick={forward10Seconds} className="w-1/3 flex items-center justify-center h-full pointer-events-auto">
          <MdForward10 onClick={forward10Seconds} className="pointer-events-auto cursor-pointer hover:fill-[#dbdbdb]" color="#ffffff" size={30} />
        </div>
      </div>

      {/* TOP HEADER CONTROLS OVERLAY */}
      <div 
        onMouseEnter={handleMouseEnterOverlay}
        onMouseLeave={handleMouseLeaveOverlay}
        className={`absolute flex justify-between top-0 w-full left-0 right-0 px-4 py-2 z-30 transition-all duration-500 ease-in-out ${
          isPlaying ? (showControls ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full") : 'opacity-100 translate-y-0'
        }`}
      >
        <button platform-target="logo-container" style={{ color: '#2e8a57' }} className="font-bold pointer-events-auto">
          <SVGLOGO className="h-[35px] md:h-[50px]" height={'50px'} fill={'#2e8a57'} />
        </button>
<div className="relative my-auto flex items-center pointer-events-auto">
  
  <button 
    onClick={(e) => { e.stopPropagation(); setShowQualityMenu(!showQualityMenu); }}
    className="flex items-center gap-1.5 bg-black/50 hover:bg-black/80 text-white font-sans text-xs md:text-sm px-2.5 py-1.5 rounded-md border border-white/10 transition-colors"
    title="Video Quality"
  >
    <MdSettings className={`animate-spin-slow ${showQualityMenu ? 'text-white' : 'text-gray-300'}`} size={16} style={{ color: '#2e8a57' }} />
    <span>{currentResolution}</span>
  </button>

  {showQualityMenu && (
    <div 
      dir='rtl'
      className="absolute overflow-y-auto max-h-[120px] sm:max-h-[150px] md:max-h-[250px] min-w-[120px] left-0 top-full mt-1.5 bg-black/90 backdrop-blur-md border border-white/10 rounded-md shadow-2xl p-1 flex flex-col z-50 animate-fade-in"
      onClick={(e) => e.stopPropagation()}
    >
      <button 
        onClick={() => handleQualityChange('Auto')}
        className={`text-center font-sans text-xs px-3 py-2 rounded transition-colors ${currentResolution.startsWith('Auto') ? 'text-white font-bold bg-white/10' : 'text-gray-300 hover:bg-white/5'}`}
        style={currentResolution.startsWith('Auto') ? { color: '#2e8a57' } : {}}
      >
        <span>Auto</span>
      </button>

      {resolutions.map((track) => {
        const isSelected = currentResolution === `${track.height}p`;
        return (
          <button
            key={track.height}
            onClick={() => handleQualityChange(track.height)}
            className={`flex flex-col items-center justify-center text-center font-sans text-xs px-3 py-1.5 rounded transition-colors ${isSelected ? 'text-white font-bold bg-white/10' : 'text-gray-300 hover:bg-white/5'}`}
            style={isSelected ? { color: '#2e8a57' } : {}}
          >
            <span>{track.height}p</span>
            
            {/* NEW: Display the exact bitrate and calculated file size */}
            <span dir='ltr' className="text-[9px] opacity-75 font-normal mt-0.5 tracking-wider" style={isSelected ? { color: '#2e8a57' } : {}}>
              {/*Math.round(track.bandwidth / 1000) kbps */}  {duration > 0 ? `~(${calculateApproxSize(track.bandwidth, duration)})` : ''}
            </span>
          </button>
        );
      })}
    </div>
  )}
</div>

        

          <button className="  py-1 px-2 md:py-2 md:px-4    rounded  bg-[#2e8a57]  text-white " onClick={()=>{ 
          if(isFullScreen) toggleFullScreen();
          else navigate(-1);
          }} >{isFullScreen ? 'خروج' :'رجوع'}</button>
      </div>

      {/* BOTTOM PROGRESS TIMELINE PANEL OVERLAY */}
      <div 
        onMouseEnter={handleMouseEnterOverlay}
        onMouseLeave={handleMouseLeaveOverlay}
        className={`absolute flex flex-col gap-1 bottom-1 left-0 right-0 z-20 px-4 py-2 bg-black/60 backdrop-blur-sm rounded-lg mx-1 transition-all duration-500 ease-in-out ${
          isPlaying ? (showControls && !showQualityMenu ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full") : 'opacity-100 translate-y-0'
        }`}
      >
        <div className='flex gap-3 w-full'>
          <div className="font-sans text-white text-sm my-auto whitespace-nowrap">
            {formatTime(currentTime)}/{formatTime(duration)}
          </div>

          <div className="my-auto relative w-full h-3 flex items-center">
            <div className="absolute w-full h-1.5 bg-gray-700 rounded-full" />
            <div className="absolute h-1.5 rounded-full transition-all duration-300 ease-out" style={{ width: `${bufferedAmount}%`, background: '#45745abc' }} />
            <div className="absolute h-1.5 rounded-full z-10" style={{ width: `${playedPercent}%`, backgroundColor: '#2e8a57' }} />
            <input type="range" min={0} max={duration || 0} value={currentTime} step="0.1" onChange={handleScrub} onMouseUp={handleScrubEnd} onTouchEnd={handleScrubEnd} className="absolute w-full h-full opacity-0 z-20 cursor-pointer appearance-none" />
            <div className="absolute w-4 h-4 bg-white border-2 rounded-full z-30 pointer-events-none shadow-md" style={{ left: `calc(${playedPercent}% - 8px)`, borderColor: '#2e8a57' }} />
          </div>
        </div>

        <div className="flex items-center mt-1 justify-between w-full">
          <div className="flex items-center gap-4">
            <button onClick={togglePlay} className="transition-colors">
              {isPlaying ? <MdPause color="#ffffff" size={25} /> : <MdPlayArrow color="#ffffff" size={25} />}
            </button>
            <div className="flex items-center group/volume gap-2">
              <button onClick={toggleMute} className="relative text-white transition-colors">
                {isMuted || volume === 0 ? <MdVolumeMute color="#ffffff" size={25} /> : (volume > 0.7 ? <MdVolumeUp color="#ffffff" size={25} /> : <MdVolumeDown color="#ffffff" size={25} />)}
              </button>
              <input type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} style={{ accentColor: '#2e8a57' }} className="w-20 h-1.5 cursor-pointer bg-white/20 rounded-full" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={toggleFullScreen}>
              {isFullScreen ? <MdFullscreenExit color="#ffffff" size={25} /> : <MdFullscreen color="#ffffff" size={25} />}
            </button>
          </div>
        </div>
      </div>
       
       {/* CONDITIONAL DEBUGGER OVERLAY */}
       {debugError && (
         <div className="absolute top-4 left-4 right-4 bg-zinc-950/95 text-red-500 border border-red-600 rounded-md p-4 font-mono text-xs z-50 shadow-2xl overflow-y-auto max-h-[80%] break-all">
           <div className="flex justify-between items-center mb-2 pb-2 border-b border-white/10">
             <span className="text-white font-bold text-sm">⚠️ [DRM Runtime Debug Panel]</span>
             <div className="flex gap-2">
               <button 
                 onClick={handleCopyErrorDetails}
                 className="bg-blue-600 hover:bg-blue-700 text-white font-sans px-2.5 py-0.5 rounded font-bold text-[10px] transition-colors"
               >
                 {copyStatus}
               </button>
               <button 
                 onClick={() => setDebugError(null)}
                 className="bg-white text-black font-sans px-2 py-0.5 rounded font-bold text-[10px]"
               >
                 Dismiss
               </button>
             </div>
           </div>
           <div className="space-y-1.5 leading-relaxed">
             <div><span className="text-gray-400">Context stage:</span> <span className="text-zinc-200">{debugError.context}</span></div>
             <div><span className="text-gray-400">Error Code:</span> <span className="text-yellow-400 font-bold text-sm">{debugError.code}</span></div>
             <div><span className="text-gray-400">Details:</span> <span className="text-white">{debugError.message}</span></div>
             <div className="pt-2 text-[10px] text-zinc-500 border-t border-white/5"><span className="font-bold">User Agent:</span> {debugError.userAgent}</div>
           </div>
         </div>
       )}
   </div>
  );
};

export default VideoPlayerDrm;